import type { ChatNavigateMessage } from "@albertine/types";
import { useEffect } from "react";
import {
    isEmbeddedInParentFrame,
    parentOriginFromLocation,
    postMessageToParentFrame,
} from "@/lib/parentFrame";

// Chat item links are Concierge URLs. Ask the host to navigate: the Concierge
// iframe parent, or the top-level iOS web view.
export function useConciergeItemLinkNavigation() {
    useEffect(function interceptItemLinkClicks() {
        document.addEventListener("click", handleConciergeItemLinkClick, true);
        return () => {
            document.removeEventListener("click", handleConciergeItemLinkClick, true);
        };
    }, []);
}

export function handleConciergeItemLinkClick(event: MouseEvent): void {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }

    const parentOrigin = parentOriginFromLocation();
    if (!parentOrigin) {
        return;
    }

    const deliversToNativeHost =
        window.isIosNativeWebView === true && window.parent === window;
    if (!isEmbeddedInParentFrame() && !deliversToNativeHost) {
        return;
    }

    const target = event.target;
    const anchor = target instanceof Element ? target.closest("a[href]") : null;
    if (!(anchor instanceof HTMLAnchorElement) || !anchor.href) {
        return;
    }

    let href: URL;
    try {
        href = new URL(anchor.href);
    } catch {
        return;
    }

    if (href.origin !== parentOrigin) {
        return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const message: ChatNavigateMessage = {
        type: "chat.navigate",
        url: anchor.href,
    };
    postMessageToParentFrame(parentOrigin, message);
}
